import { Check } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { SetupProgress, SetupStep } from '@/hooks/useSetupProgress';
import { cn } from '@/lib/utils';

/**
 * Day-1 setup checklist (IX-3929) — the full step list shown in the setup
 * panel: a status circle per step, each row linking to the surface that clears
 * it (Install → the Publish page). Fed by `useSetupProgress`.
 *
 * Three states (IX-4114): validated is green whether or not you're on it, the
 * step you're *currently on* is blue, everything else is a dashed outline. Note
 * "current" means the open page, not the first unfinished step — the panel is a
 * position indicator, so it has to agree with the address bar.
 */
interface SetupChecklistProps {
    progress: SetupProgress;
}

export function SetupChecklist({ progress }: SetupChecklistProps) {
    const { steps } = progress;
    const navigate = useNavigate();
    const { pathname } = useLocation();

    const openStep = (step: SetupStep) => {
        navigate(step.href);
    };

    return (
        <ul className="space-y-0.5">
            {steps.map((step) => {
                const isCurrent = pathname === step.href || pathname.startsWith(`${step.href}/`);
                return (
                    <li key={step.id}>
                        <button
                            type="button"
                            onClick={() => openStep(step)}
                            aria-current={isCurrent ? 'step' : undefined}
                            className={cn(
                                'flex w-full items-center gap-[11px] rounded-lg px-2 py-[9px] text-left transition-colors',
                                isCurrent ? 'bg-accent-faint' : 'hover:bg-surface-sunken'
                            )}
                        >
                            {step.done ? (
                                <span className="flex size-5 flex-none items-center justify-center rounded-full bg-success-subtle text-success">
                                    <Check className="size-3" />
                                </span>
                            ) : (
                                <span
                                    className={cn(
                                        'box-border size-5 flex-none rounded-full',
                                        isCurrent
                                            ? 'border-2 border-accent-foreground bg-accent-faint'
                                            : 'border-[1.5px] border-dashed border-border-strong'
                                    )}
                                />
                            )}
                            <span
                                className={cn(
                                    'flex-1 text-[13px]',
                                    step.done
                                        ? isCurrent
                                            ? 'font-semibold text-foreground'
                                            : 'font-normal text-muted-foreground'
                                        : isCurrent
                                          ? 'font-semibold text-accent-foreground'
                                          : 'font-medium text-muted-foreground'
                                )}
                            >
                                {step.label}
                            </span>
                            {step.optional && !step.done && (
                                <span className="text-[11px] text-foreground-subtle">Optional</span>
                            )}
                        </button>
                    </li>
                );
            })}
        </ul>
    );
}
